import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import axios from 'axios';

interface LoginProps {
  onLoginSuccess: (user: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const { instance } = useMsal();
  const [showExternalLogin, setShowExternalLogin] = useState(false);
  const [externalEmail, setExternalEmail] = useState('');
  const [externalPassword, setExternalPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

  const handleMicrosoftLogin = async () => {
    try {
      console.log('=== LOGIN DEBUG ===');
      console.log('Starting login process...');
      console.log('MSAL instance:', instance);
      console.log('Login request config:', loginRequest);

      const response = await instance.loginPopup(loginRequest);
      console.log('Login response received:', response);
      console.log('Account info:', response.account);

      // Check if user's email domain is allowed
      const userEmail = response.account?.username || '';
      console.log('User email:', userEmail);
      console.log('Email domain check:', userEmail.toLowerCase().endsWith('@cellcolabs.com'));

      if (!userEmail.toLowerCase().endsWith('@cellcolabs.com')) {
        console.log('Access denied - wrong domain');
        alert('Access denied. Only @cellcolabs.com accounts are allowed.');
        await instance.logout();
        return;
      }

      console.log('Login successful, calling onLoginSuccess');
      // Login successful
      onLoginSuccess({
        name: response.account?.name || '',
        email: userEmail,
        account: response.account,
        isExternal: false
      });
      console.log('=== LOGIN SUCCESS ===');
    } catch (error: any) {
      console.error('=== LOGIN ERROR ===');
      console.error('Login failed:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      alert('Login failed. Please try again.');
    }
  };

  const handleExternalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${backendUrl}/api/auth/external-login`, {
        email: externalEmail.toLowerCase(),
        password: externalPassword
      });

      console.log('=== EXTERNAL LOGIN SUCCESS ===');
      console.log('Response:', response.data);

      // Store token in sessionStorage
      sessionStorage.setItem('externalUserToken', response.data.token);

      // Call onLoginSuccess with external user data
      onLoginSuccess({
        name: response.data.user.name,
        email: response.data.user.email,
        account: null, // No MSAL account for external users
        isExternal: true,
        token: response.data.token
      });
    } catch (err: any) {
      console.error('External login error:', err);
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center',
        minWidth: '350px'
      }}>
        <h1 style={{ marginBottom: '10px', color: '#123250', fontFamily: 'Barlow, sans-serif' }}>
          Cellco Hub
        </h1>
        <p style={{ marginBottom: '30px', color: '#666', fontSize: '14px' }}>
          Marketing Operations Platform
        </p>

        {!showExternalLogin ? (
          <>
            <button
              onClick={handleMicrosoftLogin}
              style={{
                backgroundColor: '#0078d4',
                color: 'white',
                border: 'none',
                padding: '14px 28px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                margin: '0 auto',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 120, 212, 0.3)'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.backgroundColor = '#106ebe';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor = '#0078d4';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
                <path d="M10 0H21V10H10V0Z" fill="#F25022"/>
                <path d="M0 0H10V10H0V0Z" fill="#7FBA00"/>
                <path d="M10 10H21V21H10V10Z" fill="#00A4EF"/>
                <path d="M0 10H10V21H0V10Z" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft
            </button>
            <p style={{
              marginTop: '20px',
              fontSize: '13px',
              color: '#888',
              maxWidth: '300px',
              margin: '20px auto 0'
            }}>
              CellcoLabs team members sign in with Microsoft
            </p>

            <div style={{
              borderTop: '1px solid #e0e0e0',
              marginTop: '30px',
              paddingTop: '20px'
            }}>
              <button
                onClick={() => setShowExternalLogin(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                External partner? Sign in here
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ fontSize: '18px', marginBottom: '20px', color: '#333' }}>
              External Partner Login
            </h2>

            {error && (
              <div style={{
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '10px',
                borderRadius: '4px',
                marginBottom: '15px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleExternalLogin}>
              <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={externalEmail}
                  onChange={(e) => setExternalEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                  Password
                </label>
                <input
                  type="password"
                  value={externalPassword}
                  onChange={(e) => setExternalPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  backgroundColor: loading ? '#9ca3af' : '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '14px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div style={{
              borderTop: '1px solid #e0e0e0',
              marginTop: '30px',
              paddingTop: '20px'
            }}>
              <button
                onClick={() => {
                  setShowExternalLogin(false);
                  setError(null);
                  setExternalEmail('');
                  setExternalPassword('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Back to Microsoft login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
